import { forkJoin, Subject } from 'rxjs';
import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of, Subscription, timer } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { constants } from '../shared/constants/constants';
import { BaseService } from './base-service';
import * as moment from 'moment';
import { UserService } from './user.service';
import { MetaResponse } from '../shared/interfaces/meta-response';
import { ApplicationService } from './application.service';

@Injectable({
    providedIn: 'root',
})
export class RealTimeAlertService extends BaseService {
    private closeEventSource = new Subject<void>();
    private timerSub: Subscription = null;
    private realTimeAlertTimerSub: Subscription = null;
    closeEvent$ = this.closeEventSource.asObservable();
    onRealTimeAlertDataUpdate = new BehaviorSubject<MetaResponse>(null);
    onAlarmsDataUpdate = new BehaviorSubject<any[]>(null);

    private updateRate = 10000;

    constructor(
        protected http: HttpClient,
        protected auth: ApiService,
        protected user: UserService,
        private application: ApplicationService
    ) {
        super(http, auth, user);
    }

    destroy(): void {
        super.destroy();
        if (this.timerSub) {
            this.timerSub.unsubscribe();
            delete this.timerSub;
        }
        if (this.realTimeAlertTimerSub) {
            this.realTimeAlertTimerSub.unsubscribe();
            delete this.realTimeAlertTimerSub;
        }
    }

    notifyClose(): void {
        this.closeEventSource.next();
    }

    startRealTimeAlertValueLiveUpdate(maxOffset: number): void {
        if (this.realTimeAlertTimerSub) {
            return;
        }

        this.realTimeAlertTimerSub = timer(0, this.updateRate)
            .pipe(
                mergeMap(() => {
                    const now = moment();
                    const to = now.clone().add(5, 'minutes').format();
                    const from = now
                        .clone()
                        .subtract(maxOffset + 1, 'days')
                        .format();
                    return this.requestDataForTimeframe(from, to);
                })
            )
            .subscribe({
                next: (res: MetaResponse) => {
                    this.onRealTimeAlertDataUpdate.next(res);
                },
                error: (error) => {
                    console.error('Error in real-time alert timer subscription:', error);
                },
            });
    }

    public requestDataForTimeframe(start: string, end: string): Observable<MetaResponse> {
        if (this.application.isDemoMode()) {
            const demoPowers = [245, 230, 220, 215, 218, 240, 1150, 1280, 480, 390, 350, 320,
                3200, 2600, 410, 380, 520, 2450, 2700, 1050, 980, 560, 420, 290];
            const now = moment();
            const results = demoPowers.map((power, i) => ({
                power,
                timestamp: now.clone().subtract(23 - i, 'hours').toISOString(),
            }));
            return of({ status: 'ok', data: { results } } as unknown as MetaResponse);
        }

        const instantaneous$ = this.http
            .get<MetaResponse>(
                `${this.API_BASE_URL}${constants.api.routes.instantaneousPower}/${start}/${end}/900/max`
            )
            .pipe(
                catchError((error) => {
                    console.error('Error fetching max data:', error);
                    return of({ status: 'error', data: null } as MetaResponse);
                })
            );

        const latestValue$ = this.http
            .get<any>(`${this.API_BASE_URL}${constants.api.routes.instantaneous}`)
            .pipe(
                catchError((error) => {
                    console.error('Error fetching current value:', error);
                    return of({ status: 'error', data: null } as MetaResponse);
                })
            );

        return forkJoin([instantaneous$, latestValue$]).pipe(
            map(([maxResponse, currentResponse]) => {
                const results = maxResponse?.data?.results ?? [];

                if (currentResponse?.data?.electricity) {
                    const currentPower = currentResponse.data.electricity.power;
                    const currentTimestampLocal = currentResponse.data.electricity.timestamp;

                    results.push({
                        power: currentPower,
                        timestamp: currentTimestampLocal,
                    });
                }

                return {
                    ...maxResponse,
                    data: { results },
                } as MetaResponse;
            }),
            catchError((error) => {
                console.error('Final mapping error:', error);
                return of({ status: 'error', data: null } as MetaResponse);
            })
        );
    }
}
